import React, { useState, useEffect, useRef } from "react";
import {
  CalendarDaysIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/24/solid";

import classes from "./formCalendar.module.css";
import { dateFormatter } from "../../../../utils/dateFormatter";

/* ---------------- HELPERS ---------------- */

const getToday = () => new Date();
const formatDate = (date) => date.toISOString().slice(0, 10);

const generateMonthDays = (year, month) => {
  const firstDay = new Date(year, month, 1).getDay();
  const totalDays = new Date(year, month + 1, 0).getDate();
  const days = [];
  for (let i = 1; i <= totalDays; i++) days.push(i);
  return { firstDay, days };
};

/* ---------------- COMPONENT ---------------- */

export const FormCalendar = ({
  id,
  label,
  register,
  watch,
  setValue,
  value: standaloneValue,
  onChange,
  multiple = false,
  disabled = false,
  error,
  required = false,
}) => {
  const today = getToday();

  /* ---------- INITIAL VALUE (FIXED) ---------- */
  const initial = (() => {
    if (watch && watch(id)) return watch(id);
    if (standaloneValue) return standaloneValue;
    return multiple ? [] : "";
  })();

  const [selectedDates, setSelectedDates] = useState(
    multiple ? initial : initial ? [initial] : [],
  );

  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const calendarRef = useRef(null);

  /* ---------- SYNC WITH RHF ---------- */
  useEffect(() => {
    const value = multiple ? selectedDates : selectedDates[0] || "";

    if (setValue) {
      setValue(id, value, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
    } else if (onChange) {
      onChange(value);
    }
  }, [selectedDates, id, multiple, setValue, onChange]);

  /* ---------- CLOSE ON OUTSIDE CLICK ---------- */
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (calendarRef.current && !calendarRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  /* ---------- DAY CLICK ---------- */
  const handleDayClick = (day) => {
    if (disabled) return;

    const yyyyMMdd = `${currentYear}-${String(currentMonth + 1).padStart(
      2,
      "0",
    )}-${String(day).padStart(2, "0")}`;

    const timestamp = Math.floor(new Date(yyyyMMdd).getTime() / 1000);

    const formattedDate = dateFormatter(timestamp);

    if (multiple) {
      setSelectedDates((prev) =>
        prev.includes(formattedDate)
          ? prev.filter((d) => d !== formattedDate)
          : [...prev, formattedDate],
      );
    } else {
      setSelectedDates([formattedDate]);
      setIsOpen(false);
    }
  };

  const { firstDay, days } = generateMonthDays(currentYear, currentMonth);

  /* ---------------- RENDER ---------------- */

  return (
    <div
      ref={calendarRef}
      className={`${classes.container} ${error ? classes.error : ""} ${
        selectedDates.length ? classes.selected : ""
      } ${disabled ? classes.disabled : ""}`}
    >
      {label && (
        <label
          htmlFor={id}
          className={`${classes.floatingLabel} ${
            selectedDates.length || isOpen ? classes.floating : ""
          }`}
        >
          {label}
          {required && <span className={classes.asterisk}>*</span>}
        </label>
      )}

      <div
        className={classes.inputBox}
        onClick={() => !disabled && setIsOpen((prev) => !prev)}
      >
        <input
          id={id}
          type="text"
          readOnly
          placeholder="Select date"
          className={classes.input}
          value={multiple ? selectedDates.join(", ") : selectedDates[0] || ""}
        />
        <span className={classes.calendarIcon}>
          <CalendarDaysIcon />
        </span>
      </div>

      {/* ---------- CALENDAR ---------- */}
      {isOpen && !disabled && (
        <div className={classes.calendarDropdown}>
          <div className={classes.calendarHeader}>
            <button
              type="button"
              onClick={() => {
                if (currentMonth === 0) {
                  setCurrentMonth(11);
                  setCurrentYear((y) => y - 1);
                } else {
                  setCurrentMonth((m) => m - 1);
                }
              }}
            >
              &#8249;
            </button>

            <span>
              {new Date(currentYear, currentMonth).toLocaleString("default", {
                month: "long",
              })}{" "}
              {currentYear}
            </span>

            <button
              type="button"
              onClick={() => {
                if (currentMonth === 11) {
                  setCurrentMonth(0);
                  setCurrentYear((y) => y + 1);
                } else {
                  setCurrentMonth((m) => m + 1);
                }
              }}
            >
              &#8250;
            </button>
          </div>

          <div className={classes.daysGrid}>
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
              <div key={d} className={classes.dayName}>
                {d}
              </div>
            ))}

            {[...Array(firstDay)].map((_, i) => (
              <div key={`empty-${i}`} />
            ))}

            {days.map((day) => {
              const yyyyMMdd = `${currentYear}-${String(
                currentMonth + 1,
              ).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

              const isToday = formatDate(today) === yyyyMMdd;

              return (
                <div
                  key={day}
                  className={`${classes.day} ${isToday ? classes.today : ""}`}
                  onClick={() => handleDayClick(day)}
                >
                  {day}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ---------- RHF REGISTER ---------- */}
      {register && <input type="hidden" {...register(id)} />}

      {error && typeof error === "string" && (
        <div className={classes.errorIcon} title={error}>
          <ExclamationCircleIcon className={classes.icon} />
        </div>
      )}
    </div>
  );
};
